import Pane from 'components/Common/Pane'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import CalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import GiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import { getFormattedDateByTimezone } from 'lib/experiences/experienceUtils'

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
`

const Card = styled(Pane)`
  padding: ${rem(24)};
  border-radius: 2px;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  position: relative;
  flex-grow: 1;
`

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  margin-block-start: ${rem(20)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
  }
`

interface Props {
  experience: App.ExperienceOffer
  onBuyNowBnbl: () => void
  onGiveAsGift: () => void
}

function ExperienceOfferPageBnblAvailable(props: Props) {
  const { experience, onBuyNowBnbl, onGiveAsGift } = props
  const trackEvent = useTrackExperienceOfferEvent()

  const handleOnBuyNowBnbl = useCallback(() => {
    onBuyNowBnbl()
    trackEvent('BNBL_BUTTON_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [])

  const handleOnGiveAsGift = useCallback(() => {
    onGiveAsGift()
    trackEvent('GIVE_AS_GIFT_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [])

  const isVoucherBased = !experience.hasCalendar

  const bookUntilDate = (experience.expirationDate ?? experience.bookByDate)
  const formattedBookUntilDate = getFormattedDateByTimezone(bookUntilDate, experience.location.timezone, DMY_CASUAL_FORMAT)

  const showBNBLOption = !isVoucherBased && experience.allowBuyNowBookLater
  const showGiftCardOption = showBNBLOption || (!!experience.giftType && experience.allowGiftCard)

  if (!showBNBLOption && !showGiftCardOption) {
    return null
  }

  return <SectionContainer>
    {!isVoucherBased && <Heading variant="heading4">
      Not ready to book just yet?
    </Heading>}
    <CardsContainer>
      {showBNBLOption && <Card>
        <VerticalSpacer gap={4}>
          <CalendarIcon />
          <Heading variant="heading5">
            Buy now, choose dates later
          </Heading>
          {!!formattedBookUntilDate && <BodyText variant="medium">
            You can purchase this experience and choose dates when ready. Book until {formattedBookUntilDate}.
          </BodyText>}
          <TextButton
            kind="secondary"
            size="large"
            onClick={handleOnBuyNowBnbl}
            data-testid="experience-bnbl-buy-now-button"
          >
            Buy now
          </TextButton>
        </VerticalSpacer>
      </Card>}
      {showGiftCardOption && <Card>
        <VerticalSpacer gap={4}>
          <GiftIcon />
          <Heading variant="heading5">
            Give this experience as a gift
          </Heading>
          <BodyText variant="medium">
            Surprise a special someone with the gift of an unforgettable experience.
            {!!formattedBookUntilDate && ` They can book until ${formattedBookUntilDate}.`}
          </BodyText>
          <TextButton
            kind="secondary"
            size="large"
            onClick={handleOnGiveAsGift}
          >
            Give as gift
          </TextButton>
        </VerticalSpacer>
      </Card>}
    </CardsContainer>
  </SectionContainer>
}

export default ExperienceOfferPageBnblAvailable
